import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Button, 
  Card, 
  CardBody, 
  Divider, 
  Switch, 
  Tabs, 
  Tab, 
  Modal, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter,
  useDisclosure,
  Accordion,
  AccordionItem
} from "@heroui/react";

// Komponent zgody na pliki cookie zgodny z RODO
const CookieConsent = ({ 
  position = 'bottom',
  theme = 'light',
  compactOnMobile = true
}) => {
  // Stan lokalny
  const [showBanner, setShowBanner] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Zawsze włączone
    functional: false,
    analytics: false,
    marketing: false
  });
  const [activeTab, setActiveTab] = useState('simple');
  
  // Modal dla szczegółowych ustawień
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Sprawdź, czy zgoda została już udzielona
  useEffect(() => {
    const consentGiven = localStorage.getItem('cookieConsent');
    if (!consentGiven) {
      // Pokaż banner po krótkim opóźnieniu
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Wczytaj zapisane preferencje
      try {
        const savedPreferences = JSON.parse(localStorage.getItem('cookiePreferences'));
        if (savedPreferences) {
          setCookiePreferences(savedPreferences);
        }
      } catch (error) {
        console.error('Error parsing cookie preferences:', error);
      }
    }
  }, []);

  // Obsługa akceptacji wszystkich plików cookie
  const acceptAllCookies = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    
    setCookiePreferences(allAccepted);
    saveCookiePreferences(allAccepted);
    setShowBanner(false);
  };
  
  // Obsługa akceptacji tylko niezbędnych plików cookie
  const acceptNecessaryCookies = () => {
    const necessaryOnly = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    
    setCookiePreferences(necessaryOnly);
    saveCookiePreferences(necessaryOnly);
    setShowBanner(false);
  };
  
  // Obsługa zapisywania preferencji plików cookie
  const saveCookiePreferences = (preferences) => {
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    
    // Tutaj można dodać implementację ustawienia odpowiednich flag dla zewnętrznych usług
    // np. Google Analytics, Facebook Pixel, itp.
    
    // Przykład dla Google Analytics
    if (preferences.analytics) {
      window.dataLayer = window.dataLayer || [];
      function gtag(){window.dataLayer.push(arguments);}
      gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    } else {
      window.dataLayer = window.dataLayer || [];
      function gtag(){window.dataLayer.push(arguments);}
      gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
    
    // Przykład dla piksela Facebook
    if (preferences.marketing) {
      window.fbq && window.fbq('consent', 'grant');
    } else {
      window.fbq && window.fbq('consent', 'revoke');
    }
    
    // Emituj zdarzenie dla innych komponentów
    const event = new CustomEvent('cookieConsentUpdated', { detail: preferences });
    window.dispatchEvent(event);
  };
  
  // Obsługa zmiany pojedynczych przełączników
  const handleSwitchChange = (category) => {
    const newPreferences = {
      ...cookiePreferences,
      [category]: !cookiePreferences[category]
    };
    setCookiePreferences(newPreferences);
  };
  
  // Obsługa zapisywania ustawień z modalu
  const savePreferencesFromModal = () => {
    saveCookiePreferences(cookiePreferences);
    onClose();
    setShowBanner(false);
  };
  
  // Resetuj cookie consent (funkcja do testów)
  const resetCookieConsent = () => {
    localStorage.removeItem('cookieConsent');
    localStorage.removeItem('cookiePreferences');
    setShowBanner(true);
    setCookiePreferences({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    });
  };
  
  // Pozycja bannera
  const positionClasses = {
    bottom: 'bottom-0 left-0 right-0',
    top: 'top-0 left-0 right-0',
    'bottom-left': 'bottom-4 left-4 max-w-md',
    'bottom-right': 'bottom-4 right-4 max-w-md'
  };
  
  // Animacje
  const bannerVariants = {
    hidden: { 
      y: position.includes('bottom') ? 100 : -100, 
      opacity: 0 
    },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        type: 'spring',
        damping: 25,
        stiffness: 300,
        duration: 0.4
      }
    },
    exit: { 
      y: position.includes('bottom') ? 100 : -100, 
      opacity: 0,
      transition: {
        duration: 0.3
      }
    }
  };
  
  // Opisy kategorii plików cookie
  const cookieCategories = [
    {
      id: 'necessary',
      title: 'Niezbędne',
      description: 'Te pliki cookie są niezbędne do prawidłowego funkcjonowania strony internetowej i nie mogą być wyłączone.',
      alwaysOn: true
    },
    {
      id: 'functional',
      title: 'Funkcjonalne',
      description: 'Te pliki cookie umożliwiają stronie zapamiętanie wyborów, których dokonujesz (np. preferowany język lub region, w którym się znajdujesz) i zapewniają ulepszone, bardziej spersonalizowane funkcje.'
    },
    {
      id: 'analytics',
      title: 'Analityczne',
      description: 'Te pliki cookie pomagają nam zrozumieć, w jaki sposób odwiedzający wchodzą w interakcję z naszą witryną, zbierając i raportując informacje anonimowo.'
    },
    {
      id: 'marketing',
      title: 'Marketingowe',
      description: 'Te pliki cookie są używane do śledzenia odwiedzających na stronach internetowych. Zamiarem jest wyświetlanie reklam, które są odpowiednie i angażujące dla poszczególnych użytkowników.'
    }
  ];

  return (
    <>
      {/* Banner główny */}
      <AnimatePresence>
        {showBanner && (
          <motion.div
            className={`fixed ${positionClasses[position] || positionClasses.bottom} z-50 p-4 md:p-6`}
            variants={bannerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Card 
              className={`shadow-lg border border-divider ${position.includes('bottom') ? 'rounded-b-none' : 'rounded-t-none'} w-full`}
              isBlurred
            >
              <CardBody className={compactOnMobile ? 'p-3 md:p-6' : 'p-6'}>
                <div className={compactOnMobile ? 'flex flex-col md:flex-row gap-4 items-start' : 'flex flex-col gap-4'}>
                  <div className="flex-grow">
                    <h3 className="text-lg font-semibold mb-2">Szanujemy Twoją prywatność</h3>
                    <p className="text-foreground-muted text-sm mb-2">
                      Używamy plików cookie, aby zapewnić najlepsze wrażenia podczas korzystania z naszej strony. 
                      Możesz wybrać, które rodzaje plików cookie akceptujesz.
                    </p>
                    
                    <Tabs 
                      aria-label="Opcje cookie"
                      selectedKey={activeTab}
                      onSelectionChange={setActiveTab}
                      size="sm"
                      color="primary"
                      variant="underlined"
                      className="mt-2"
                    >
                      <Tab key="simple" title="Proste" />
                      <Tab key="detailed" title="Szczegółowe" />
                    </Tabs>
                    
                    {activeTab === 'simple' ? (
                      <div className="mt-2 text-sm text-foreground-muted">
                        <p>
                          Niezbędne pliki cookie są zawsze włączone, ponieważ są wymagane do prawidłowego działania strony.
                          Możesz zaakceptować wszystkie pliki cookie lub dostosować swoje preferencje.
                        </p>
                      </div>
                    ) : (
                      <div className="mt-4 space-y-3">
                        {cookieCategories.map((category) => (
                          <div key={category.id} className="flex justify-between items-center">
                            <div>
                              <p className="font-medium text-sm">{category.title}</p>
                              <p className="text-xs text-foreground-muted truncate max-w-xs">
                                {category.description.length > 60 
                                  ? `${category.description.slice(0, 60)}...` 
                                  : category.description
                                }
                              </p>
                            </div>
                            <Switch
                              isSelected={cookiePreferences[category.id]}
                              onChange={() => handleSwitchChange(category.id)}
                              size="sm"
                              isDisabled={category.alwaysOn}
                              color="primary"
                            />
                          </div>
                        ))}
                        <Button 
                          size="sm" 
                          variant="light" 
                          onPress={onOpen}
                          className="text-primary"
                        >
                          Więcej opcji
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  <div className={`flex ${compactOnMobile ? 'flex-row md:flex-col' : 'flex-col'} gap-2 mt-2 md:mt-0`}>
                    <Button
                      color="primary"
                      onPress={acceptAllCookies}
                      fullWidth
                      size={compactOnMobile ? 'sm' : 'md'}
                    >
                      Akceptuj wszystkie
                    </Button>
                    {activeTab === 'simple' && (
                      <Button
                        variant="bordered"
                        color="primary"
                        onPress={activeTab === 'simple' ? acceptNecessaryCookies : savePreferencesFromModal}
                        fullWidth
                        size={compactOnMobile ? 'sm' : 'md'}
                      >
                        Tylko niezbędne
                      </Button>
                    )}
                    {activeTab === 'detailed' && (
                      <Button
                        variant="bordered"
                        color="primary"
                        onPress={savePreferencesFromModal}
                        fullWidth
                        size={compactOnMobile ? 'sm' : 'md'}
                      >
                        Zapisz preferencje
                      </Button>
                    )}
                  </div>
                </div>
                
                <div className="mt-4 text-xs text-foreground-muted">
                  <span>Kontynuując korzystanie z tej strony, zgadzasz się na naszą </span>
                  <Link to="/polityka-prywatnosci" className="text-primary underline">Politykę Prywatności</Link>.
                </div>
              </CardBody>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modal ze szczegółowymi opcjami */}
      <Modal 
        isOpen={isOpen} 
        onClose={onClose}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                Ustawienia plików cookie
              </ModalHeader>
              <ModalBody>
                <p className="text-foreground-muted mb-4">
                  Poniżej znajdziesz szczegółowe informacje na temat różnych rodzajów plików cookie i możesz wybrać, 
                  które z nich chcesz zaakceptować podczas przeglądania naszej strony.
                </p>
                
                <Accordion variant="splitted" className="mb-6">
                  {cookieCategories.map((category) => (
                    <AccordionItem
                      key={category.id}
                      aria-label={category.title}
                      title={
                        <div className="flex justify-between items-center w-full">
                          <span className="font-semibold">{category.title}</span>
                          <Switch
                            isSelected={cookiePreferences[category.id]}
                            onChange={() => handleSwitchChange(category.id)}
                            isDisabled={category.alwaysOn}
                            color="primary"
                            className="mx-4"
                          />
                        </div>
                      }
                      className="mb-2"
                    >
                      <div className="py-2">
                        <p className="text-foreground-muted mb-3">{category.description}</p>
                        
                        {category.id === 'necessary' && (
                          <div className="bg-primary/5 p-3 rounded-md">
                            <h4 className="text-sm font-medium mb-1">Przykładowe pliki cookie:</h4>
                            <ul className="text-xs text-foreground-muted space-y-1 ml-4 list-disc">
                              <li>Sesja logowania</li>
                              <li>Zapamiętanie zgody na pliki cookie</li>
                              <li>Podstawowe funkcje koszyka</li>
                              <li>Zabezpieczenia formularzy</li>
                            </ul>
                          </div>
                        )}
                        
                        {category.id === 'functional' && (
                          <div className="bg-primary/5 p-3 rounded-md">
                            <h4 className="text-sm font-medium mb-1">Przykładowe pliki cookie:</h4>
                            <ul className="text-xs text-foreground-muted space-y-1 ml-4 list-disc">
                              <li>Preferencje języka</li>
                              <li>Historia przeglądania</li>
                              <li>Niestandardowe ustawienia strony</li>
                              <li>Zapisane formularze</li>
                            </ul>
                          </div>
                        )}
                        
                        {category.id === 'analytics' && (
                          <div className="bg-primary/5 p-3 rounded-md">
                            <h4 className="text-sm font-medium mb-1">Przykładowe pliki cookie:</h4>
                            <ul className="text-xs text-foreground-muted space-y-1 ml-4 list-disc">
                              <li>Google Analytics</li>
                              <li>Hotjar</li>
                              <li>Matomo</li>
                              <li>Dane o wydajności strony</li>
                            </ul>
                          </div>
                        )}
                        
                        {category.id === 'marketing' && (
                          <div className="bg-primary/5 p-3 rounded-md">
                            <h4 className="text-sm font-medium mb-1">Przykładowe pliki cookie:</h4>
                            <ul className="text-xs text-foreground-muted space-y-1 ml-4 list-disc">
                              <li>Google Ads</li>
                              <li>Facebook Pixel</li>
                              <li>LinkedIn Insight</li>
                              <li>Remarketing</li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </AccordionItem>
                  ))}
                </Accordion>
                
                <Divider className="my-4" />
                
                <div className="text-xs text-foreground-muted">
                  <p className="mb-2">
                    Twoje wybory zostaną zapisane i będą obowiązywać podczas korzystania z naszej strony.
                    Możesz w dowolnym momencie zmienić swoje preferencje dotyczące plików cookie, klikając
                    link "Ustawienia cookie" w stopce strony.
                  </p>
                  <p>
                    Więcej informacji na temat sposobu, w jaki wykorzystujemy pliki cookie i Twoje dane osobowe,
                    znajdziesz w naszej <Link to="/polityka-prywatnosci" className="text-primary underline">Polityce Prywatności</Link>.
                  </p>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="bordered" color="danger" onPress={onClose}>
                  Anuluj
                </Button>
                <Button color="primary" onPress={savePreferencesFromModal}>
                  Zapisz preferencje
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      
      {/* Przycisk resetowania zgód (tylko dla celów testowych)
      {process.env.NODE_ENV === 'development' && !showBanner && (
        <button
          onClick={resetCookieConsent}
          className="fixed bottom-2 left-2 bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-md z-50 opacity-50 hover:opacity-100"
        >
          Reset Cookie Consent
        </button>
      )}*/}
    </>
  );
};

export default CookieConsent;
